import React, { useState, useEffect } from 'react'
import { Info } from 'lucide-react'
import useAudienceTargetingStore from '@/store/audienceTargetingStore'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { FilterIcon } from '@/components/icons'
import {
    LEAD_EVENT_TYPES,
    SALES_EVENT_TYPES,
    DEFAULT_EVENT_TYPES,
    AUDIENCE_CREATION_SETTING_NOTE,
} from '@/utils/ad-campaign-constants'

interface EventTypeSettingsProps {
    stage: string
    audience: string
    getStepContentData: (step: string) => any
    /** Show the stored value without letting it change (published ad sets in edit mode). */
    readOnly?: boolean
}

const EventTypeSettings: React.FC<EventTypeSettingsProps> = ({
    stage,
    audience,
    getStepContentData,
    readOnly = false,
}) => {
    const { getAudienceSettings, updateAudienceSetting } = useAudienceTargetingStore()
    const campaignObjectiveData = getStepContentData('campaignObjective') || {}
    const selectedObjective = campaignObjectiveData.selectedObjective?.toLowerCase()

    // Only render for leads and sales campaigns
    if (!selectedObjective || !['leads', 'sales'].includes(selectedObjective)) {
        return null
    }

    // Get current settings
    const audienceSettings = getAudienceSettings(stage, audience)
    const storedEventType = audienceSettings?.custom_event_type

    // Determine default value and available options based on objective
    const defaultEventType = DEFAULT_EVENT_TYPES[selectedObjective as keyof typeof DEFAULT_EVENT_TYPES]
    const eventOptions = selectedObjective === 'leads' ? LEAD_EVENT_TYPES : SALES_EVENT_TYPES

    const [selectedEventType, setSelectedEventType] = useState<string>(storedEventType || defaultEventType)

    // Initialize from stored value or set default
    useEffect(() => {
        if (readOnly) return
        if (storedEventType) {
            setSelectedEventType(storedEventType)
        } else if (defaultEventType) {
            setSelectedEventType(defaultEventType)
            updateAudienceSetting(stage, audience, 'custom_event_type', defaultEventType)
        }
    }, [storedEventType, defaultEventType, stage, audience, updateAudienceSetting, readOnly])

    const handleEventTypeChange = (eventType: string) => {
        setSelectedEventType(eventType)
        updateAudienceSetting(stage, audience, 'custom_event_type', eventType)
    }

    const selectedOption = eventOptions.filter(
        (x) => x.value === (readOnly ? storedEventType || defaultEventType : selectedEventType),
    )[0]

    return (
        <div className="tw:flex tw:gap-6 tw:flex-col tw:bg-secondary-background tw:rounded-xs">
            <div className="tw:flex tw:gap-1.5 tw:items-center tw:p-3 tw:pb-0">
                <FilterIcon className="tw:w-6 tw:h-6 tw:text-gray-200" />
                <span className="tw:font-semibold">Event Type</span>
                <Tooltip>
                    <TooltipTrigger>
                        <Info className="tw:w-4 tw:h-4 tw:text-gray-200" />
                    </TooltipTrigger>
                    <TooltipContent className="tw:max-w-80" showArrow={false}>
                        <div className="tw:text-sm tw:text-gray-400">
                            Select the specific event type for your {selectedObjective} campaign.
                        </div>
                    </TooltipContent>
                </Tooltip>
            </div>

            {readOnly ? (
                <div className="tw:flex tw:flex-col tw:gap-1 tw:p-3 tw:pt-0" data-testid="event-type-read-only">
                    <span className="tw:font-medium">{selectedOption?.label ?? storedEventType}</span>
                    <span className="tw:text-sm tw:text-gray-200">{AUDIENCE_CREATION_SETTING_NOTE}</span>
                </div>
            ) : (
                <div className="tw:p-3 tw:pt-0">
                    <Select value={selectedEventType} onValueChange={handleEventTypeChange}>
                        <SelectTrigger className="tw:w-full">
                            <SelectValue placeholder={`Select event type for ${selectedObjective} campaign`}>
                                {selectedOption?.label}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="tw:bg-primary tw:border-slate-700">
                            {eventOptions.map((option) => (
                                <SelectItem
                                    key={option.value}
                                    value={option.value}
                                    className="tw:text-white tw:hover:bg-slate-700"
                                    handleDelete={() => {}}
                                >
                                    <div className="tw:flex tw:flex-col tw:gap-0.5">
                                        <span className="tw:font-medium">{option.label}</span>
                                        <span className="tw:text-sm tw:text-gray-200">{option.description}</span>
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}
        </div>
    )
}

export default EventTypeSettings
